import React from 'react';
import {
  useLocalization,
  useLocalizationContext,
} from '@im/react-localization';
import { IngramLogo } from '@im/ui-lib/icons/IngramLogo';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSiteConfig } from 'mf_core/config';
import { useSocialMedias } from '../IngramFooter/useSocialMedias';
import { useIsResolution } from '@im/ui-lib/responsive';
import { AppQrCode } from './appQrCode';
import Grid from '@mui/material/Grid';

export const IngramMobile = () => {
  const { siteCode } = useSiteConfig();

  const whitelistedCountries = [
    'at',
    'us',
    'ca',
    'gb',
    'in',
    'mx',
    'de',
    'it',
    'es',
    'fr',
  ];

  const isNewHeader = whitelistedCountries.includes(siteCode);
  const anroidLink =
    'https://play.google.com/store/apps/details?id=com.ingrammicro.cep';

  const { translate } = useLocalization();

  const isTablet = useIsResolution({ min: 599 });

  const { locale } = useLocalizationContext();
  const socialMedias = useSocialMedias(locale);
  const buildTrackingUrl = (url: string) => {
    if (!url) return '';
    const appStore = url.includes('ios.html') ? 'ios' : 'android';
    return `${url}&UTM_Campaign=x4c_mweb_browser_${appStore}`;
  };

  const appStoreLinks = socialMedias?.appStore?.map((appStore) => ({
    ...appStore,
    url: buildTrackingUrl(appStore.url),
  }));

  return (
    <>
      <Box padding={{ sm: 4, xs: '10px 15px' }}>
        <IngramLogo width="242" />
      </Box>
      {isNewHeader ? (
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: '#f8f8f8',
            display: 'flex',
            justifyContent: 'center',
            padding: '32px 40px 24px 40px',
            paddingBottom: isTablet ? '78px' : '24px',
            paddingTop: isTablet ? '88px' : '32px',
            verticalAlign: 'middle',
          }}
        >
          <Stack
            maxWidth={{
              sm: '460px',
              xs: '295px',
            }}
            sx={{
              display: 'flex',
              gap: '16px',
              textAlign: 'center',
            }}
          >
            <a href={anroidLink}>
              <img alt="Ingram Logo" src="/cep/assets/img/ingram-x-logo.png" />
            </a>
            <Typography
              sx={{ fontFamily: 'inter', fontSize: '18px', fontWeight: 600 }}
            >
              {translate('MobileWeb.LoginPageDescriptionLine1Text')}
            </Typography>
            <Typography
              sx={{ fontFamily: 'inter', fontSize: '12px', fontWeight: 400 }}
            >
              <Typography
                sx={{
                  display: 'inline',
                  fontFamily: 'inter',
                  fontSize: '12px',
                }}
              >
                {translate('MobileWeb.LoginPageDescriptionLine2Text')}{' '}
              </Typography>
              <Typography
                sx={{
                  display: 'inline',
                  fontFamily: 'inter',
                  fontSize: '12px',
                  fontWeight: 600,
                }}
              >
                {translate('MobileWeb.LoginPageDescriptionLine2BoldText')}{' '}
              </Typography>
              <Typography
                sx={{
                  display: 'inline',
                  fontFamily: 'inter',
                  fontSize: '12px',
                }}
              >
                {translate('MobileWeb.LoginPageDescriptionLine3Text')}
              </Typography>
            </Typography>
            <Grid
              display={'flex'}
              gap={'24px'}
              justifyContent={'center'}
              paddingY={'8px'}
            >
              <Stack
                direction="row"
                gap={isTablet ? '12px' : '16px'}
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: '10px',
                }}
              >
                {appStoreLinks?.map((item) => (
                  <a
                    data-name={`${item.name}-X4CMWeb`}
                    href={item.url}
                    key={item.name}
                  >
                    <img
                      alt={item.name}
                      src={item.iconImage}
                      style={{ height: '40px' }}
                    />
                  </a>
                ))}
              </Stack>
              {isTablet && <AppQrCode size={85} />}
            </Grid>
          </Stack>
        </Box>
      ) : (
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: '#f8f8f8',
            justifyContent: 'center',
            padding: '10px 20px',
            verticalAlign: 'middle',
          }}
        >
          <Typography
            align="center"
            color={(theme) => theme.palette.text.secondary}
            sx={{ fontSize: '24px', fontWeight: 400 }}
          >
            Please visit on a larger device
          </Typography>
          <Typography
            align="center"
            color={(theme) => theme.palette.text.secondary}
            sx={{ fontSize: '14px', fontWeight: 400 }}
          >
            Ingram Micro Xvantage isn't currently available for mobile devices
          </Typography>
        </Box>
      )}
      <Stack
        direction={{ sm: 'row', xs: 'column' }}
        justifyContent={{ sm: 'space-between', xs: 'start' }}
        sx={{
          backgroundImage: `url('/cep/assets/img/login/login-header.png')`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          height: 325,
        }}
      ></Stack>
      <Box>
        <Typography
          align="center"
          color={(theme) => theme.palette.text.secondary}
          padding={1}
          variant="body2"
        >
          {translate('OktaWidget.Copyright', {
            year: new Date().getFullYear().toString(),
          })}
        </Typography>
      </Box>
    </>
  );
};
